import { useCallback, useEffect, useState } from 'react'
import {
  api,
  ApiError,
  type Channel,
  type ChannelKind,
  type Warehouse,
} from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { Button, ConfirmDialog, Field, Input, Notice, Select, Spinner } from '../ui/controls'

/**
 * Мои магазины: подключённые каналы продаж.
 *
 * Компания торгует не только на своей витрине, и мест у одного
 * маркетплейса бывает несколько: два магазина на Kaspi с разными
 * кабинетами. Товары и остатки при этом общие — канал лишь указывает,
 * остаток какого склада отдавать.
 *
 * Токен вводится, но никогда не показывается обратно: после сохранения
 * от него остаются лишь последние знаки, чтобы владелец узнал свой среди
 * нескольких. Поле правки пустое означает «оставить прежний».
 */
export default function StoresPage() {
  const { state } = useAuth()
  const canWrite = state.status === 'authenticated' && state.me.permissions.write.catalog

  const [kinds, setKinds] = useState<ChannelKind[]>([])
  const [secretsReady, setSecretsReady] = useState(true)
  const [channels, setChannels] = useState<Channel[] | null>(null)
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [notice, setNotice] = useState<{ kind: 'error' | 'success' | 'warning'; text: string } | null>(null)
  const [pending, setPending] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<Channel | null>(null)

  type Draft = {
    id: number | null
    kind: string
    name: string
    token: string
    merchant_id: string
    warehouse_id: number | null
  }
  const [draft, setDraft] = useState<Draft | null>(null)

  const load = useCallback(async () => {
    setChannels(await api.channels())
  }, [])

  useEffect(() => {
    void api.channelKinds().then((r) => {
      setKinds(r.kinds)
      setSecretsReady(r.secrets_ready)
    })
    void api.warehouses().then(setWarehouses)
    void load().catch((e) =>
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Не удалось загрузить' }),
    )
  }, [load])

  async function run<T>(fn: () => Promise<T>, done?: (v: T) => void) {
    setPending(true)
    setNotice(null)
    try {
      const v = await fn()
      await load()
      done?.(v)
    } catch (e) {
      setNotice({ kind: 'error', text: e instanceof ApiError ? e.message : 'Не удалось' })
    } finally {
      setPending(false)
    }
  }

  if (channels === null) return <Spinner label="Загрузка магазинов…" />

  return (
    <section className="max-w-5xl">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">Мои магазины</h2>
          <p className="mt-0.5 text-sm text-ink-500">
            Площадки, где продаются те же товары. Остатки и каталог общие —
            магазин лишь указывает, остаток какого склада отдавать.
          </p>
        </div>
        {canWrite && draft === null && secretsReady && (
          <Button
            variant="primary"
            onClick={() =>
              setDraft({ id: null, kind: 'kaspi', name: '', token: '', merchant_id: '', warehouse_id: null })
            }
          >
            Подключить магазин
          </Button>
        )}
      </header>

      {notice && (
        <Notice kind={notice.kind} onClose={() => setNotice(null)}>
          {notice.text}
        </Notice>
      )}

      {!secretsReady && (
        <Notice kind="warning">
          Не задан ключ шифрования <span className="font-mono">APP_SECRET_KEY</span>. Пока его нет,
          подключить магазин нельзя: хранить чужой токен в открытом виде недопустимо.
          Сгенерируйте ключ командой <span className="font-mono">openssl rand -base64 32</span> и
          положите его в <span className="font-mono">deploy/.env</span>.
        </Notice>
      )}

      {draft !== null && (
        <div className="mb-6 rounded-lg border border-ink-200 bg-white p-4 dark:border-ink-800 dark:bg-ink-950">
          <h3 className="mb-3 text-sm font-semibold text-ink-900 dark:text-ink-50">
            {draft.id === null ? 'Новый магазин' : 'Настройки магазина'}
          </h3>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Маркетплейс">
              <Select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
                disabled={draft.id !== null}
                className="w-full rounded-md border border-ink-300 bg-white px-2.5 py-2 text-sm text-ink-900 disabled:opacity-60 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50"
              >
                {kinds.map((k) => (
                  <option key={k.kind} value={k.kind} disabled={!k.ready}>
                    {k.name}
                    {k.ready ? '' : ' — скоро'}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Название" hint="Своё, чтобы различать магазины">
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Основной магазин"
                autoFocus
              />
            </Field>

            <Field
              label="Токен доступа"
              hint={
                draft.id === null
                  ? kinds.find((k) => k.kind === draft.kind)?.hint
                  : 'Пусто — оставить прежний токен'
              }
            >
              <Input
                type="password"
                value={draft.token}
                onChange={(e) => setDraft({ ...draft, token: e.target.value })}
                placeholder={draft.id === null ? 'вставьте токен из кабинета' : '••••••••'}
                autoComplete="off"
                className="font-mono"
              />
            </Field>

            <Field label="Код продавца" hint="Необязательно, из адреса витрины">
              <Input
                value={draft.merchant_id}
                onChange={(e) => setDraft({ ...draft, merchant_id: e.target.value })}
                placeholder="9759027"
                className="font-mono"
              />
            </Field>

            <Field label="Остатки склада" hint="Какой склад отдаётся этому магазину">
              <Select value={draft.warehouse_id ?? ''} onChange={(e) =>
                  setDraft({ ...draft, warehouse_id: e.target.value ? Number(e.target.value) : null })
                }
                className="w-full rounded-md border border-ink-300 bg-white px-2.5 py-2 text-sm text-ink-900 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50"
              >
                <option value="">Все склады витрины</option>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <Button
              variant="primary"
              disabled={
                pending ||
                draft.name.trim() === '' ||
                (draft.id === null && draft.token.trim() === '')
              }
              onClick={() =>
                void run(
                  () =>
                    draft.id === null
                      ? api.createChannel({
                          kind: draft.kind,
                          name: draft.name.trim(),
                          token: draft.token.trim(),
                          merchant_id: draft.merchant_id.trim() || null,
                          warehouse_id: draft.warehouse_id,
                        })
                      : api.updateChannel(draft.id, {
                          name: draft.name.trim(),
                          token: draft.token.trim() || undefined,
                          merchant_id: draft.merchant_id.trim() || null,
                          warehouse_id: draft.warehouse_id,
                        }),
                  () => {
                    setDraft(null)
                    setNotice({
                      kind: 'success',
                      text: 'Магазин сохранён. Проверьте связь, чтобы убедиться в верности токена.',
                    })
                  },
                )
              }
            >
              Сохранить
            </Button>
            <button
              type="button"
              onClick={() => setDraft(null)}
              className="text-sm text-ink-500 hover:text-ink-900 dark:hover:text-ink-50"
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      {channels.length === 0 ? (
        <div className="rounded-lg border border-dashed border-ink-300 px-6 py-10 text-center dark:border-ink-700">
          <p className="text-sm text-ink-700 dark:text-ink-300">Магазины не подключены</p>
          <p className="mx-auto mt-1.5 max-w-lg text-sm text-ink-500">
            Подключите кабинет продавца, чтобы принимать оттуда заказы и отдавать
            остатки. Токен создаётся в кабинете Kaspi: Настройки → Токен API.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {channels.map((c) => (
            <li
              key={c.id}
              className="rounded-lg border border-ink-200 bg-white p-4 dark:border-ink-800 dark:bg-ink-950"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-medium text-ink-900 dark:text-ink-50">{c.name}</span>
                    <span className="text-xs uppercase text-ink-500">
                      {kinds.find((k) => k.kind === c.kind)?.name ?? c.kind}
                    </span>
                    <StateTag state={c.state} />
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-500">
                    <span>
                      токен{' '}
                      <span className="font-mono">
                        {c.has_token ? c.token_hint : 'не задан'}
                      </span>
                    </span>
                    {c.merchant_id && <span className="font-mono">код {c.merchant_id}</span>}
                    <span>остатки: {c.warehouse_name ?? 'все склады витрины'}</span>
                    <span>заказов: {c.orders}</span>
                    {c.last_check_at && (
                      <span>проверен {c.last_check_at.slice(0, 16).replace('T', ' ')}</span>
                    )}
                  </div>
                  {c.state === 'error' && c.last_error && (
                    <p className="mt-2 text-sm text-danger-700 dark:text-danger-100">
                      {c.last_error}
                    </p>
                  )}
                </div>

                {canWrite && (
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <Button
                      onClick={() =>
                        void run(() => api.checkChannel(c.id), () =>
                          setNotice({ kind: 'success', text: `Связь с «${c.name}» установлена` }),
                        )
                      }
                      disabled={pending || !c.has_token}
                    >
                      Проверить связь
                    </Button>
                    <button
                      type="button"
                      onClick={() =>
                        setDraft({
                          id: c.id,
                          kind: c.kind,
                          name: c.name,
                          token: '',
                          merchant_id: c.merchant_id ?? '',
                          warehouse_id: c.warehouse_id,
                        })
                      }
                      className="text-sm text-ink-500 hover:text-ink-900 dark:hover:text-ink-50"
                    >
                      Настроить
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        void run(() => api.enableChannel(c.id, c.state === 'disabled'))
                      }
                      disabled={pending}
                      className="text-sm text-ink-500 hover:text-ink-900 dark:hover:text-ink-50"
                    >
                      {c.state === 'disabled' ? 'Включить' : 'Выключить'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(c)}
                      className="text-sm text-ink-500 hover:text-danger-700 dark:hover:text-danger-100"
                    >
                      Удалить
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={`Отключить магазин «${confirmDelete.name}»?`}
          message={
            confirmDelete.orders > 0
              ? `Заказы (${confirmDelete.orders}) останутся в системе, но связь с кабинетом будет утеряна: менять их состояние на маркетплейсе станет нельзя.`
              : 'Токен будет удалён. Чтобы подключить магазин снова, понадобится ввести его заново.'
          }
          confirmLabel="Отключить"
          pending={pending}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => {
            const c = confirmDelete
            setConfirmDelete(null)
            void run(() => api.deleteChannel(c.id), () =>
              setNotice({ kind: 'warning', text: `Магазин «${c.name}» отключён` }),
            )
          }}
        />
      )}
    </section>
  )
}

function StateTag({ state }: { state: Channel['state'] }) {
  const map: Record<string, { text: string; cls: string }> = {
    draft: { text: 'не проверен', cls: 'bg-warning-100 text-warning-700 dark:bg-warning-700/25 dark:text-warning-100' },
    active: { text: 'работает', cls: 'bg-success-100 text-success-700 dark:bg-success-700/25 dark:text-success-100' },
    error: { text: 'ошибка связи', cls: 'bg-danger-100 text-danger-700 dark:bg-danger-700/25 dark:text-danger-100' },
    disabled: { text: 'выключен', cls: 'bg-ink-100 text-ink-500 dark:bg-ink-800 dark:text-ink-400' },
  }
  const v = map[state] ?? { text: state, cls: '' }
  return <span className={`rounded-full px-2 py-0.5 text-xs ${v.cls}`}>{v.text}</span>
}
